import { useState } from 'react';
import { getContainer } from '@framework/di/getContainer';
import { useUserPlanContext } from '@/creditflowresearch/application/User/UserPlan/UserPlanContext';
import { AlertManager } from '@creditflowresearch/uilib/Alert';
import { getErrorMessage } from '@framework/message/Mapper/getMessage';
import ResearchRepository from '@/creditflowresearch/infrastructure/repository/ResearchRepository';
import UserPlan from '@creditflowresearch/user-plan/UserPlan';
import Dispatcher from '@dealroadshow/json-rpc-dispatcher';
import { makeEmptyGroupIds } from '@/creditflowresearch/application/Research/utils';
import { filterResearchResponse } from '@/creditflowresearch/application/Research/serverSide/helpers';

export const useResearchDataFetching = () => {
  const container = getContainer();
  const { plan } = useUserPlanContext();
  const researchRepository = container.get(ResearchRepository);

  const [isFetchingDocument, setIsFetchingDocument] = useState(false);
  const [isFetchingResearchList, setIsFetchingResearchList] = useState(false);
  const [isFetchingAllGroups, setIsFetchingAllGroups] = useState(false);

  const shouldSkipRequest = (isFirstFetch) => {
    return isFirstFetch || UserPlan.isUnauthorized(plan);
  };

  const getDocumentById = async (documentId) => {
    let response;
    setIsFetchingDocument(true);
    try {
      let documentWindow = window.open('', '_blank');
      response = await researchRepository.getResearchDocument(documentId);
      documentWindow.focus();

      if (response.documentUrl) {
        documentWindow.location = response.documentUrl;
      } else {
        documentWindow.close();
      }
    } catch (error) {
      if (!Dispatcher.isAbortError(error)) {
        AlertManager.error(getErrorMessage(error));
      }
    } finally {
      setIsFetchingDocument(false);
    }
    return response;
  };

  const getResearchList = async (payload, setResearchData, isFirstFetch) => {
    if (shouldSkipRequest(isFirstFetch)) {
      return;
    }

    setIsFetchingResearchList(true);
    try {
      const response = await researchRepository.getResearchList(payload);
      setResearchData((prev) => ({
        ...prev,
        ...response,
        page: payload.page || prev.page,
        perPage: payload.perPage || prev.perPage,
      }));
    } catch (error) {
      if (!Dispatcher.isAbortError(error)) {
        AlertManager.error(getErrorMessage(error));
      }
    } finally {
      setIsFetchingResearchList(false);
    }
  };

  const getResearchForAllGroups = async (payload, setResearchData, setEmptyDocumentGroupIds, isFirstFetch) => {
    if (shouldSkipRequest(isFirstFetch)) {
      return;
    }

    setIsFetchingAllGroups(true);
    try {
      const response = await researchRepository.getResearchTileData(payload);
      const emptyGroupIds = makeEmptyGroupIds(response);
      setEmptyDocumentGroupIds(emptyGroupIds);
      setResearchData((prev) => ({
        ...prev,
        ...filterResearchResponse(response, payload.filter.includedDocumentGroupId),
      }));
    } catch (error) {
      if (!Dispatcher.isAbortError(error)) {
        AlertManager.error(getErrorMessage(error));
      }
    } finally {
      setIsFetchingAllGroups(false);
    }
  };

  return {
    getDocumentById,
    getResearchList,
    getResearchForAllGroups,
    isFetchingDocument,
    isFetchingResearchList,
    isFetchingAllGroups,
  };
};
